import { Alert, Button, Input, Modal, Space, Table, Tag, Typography, Upload, message } from "antd";
import React, { useState } from "react";
import { MdUploadFile } from "react-icons/md";
import { request } from "../../functions/request";

const { Text } = Typography;

/**
 * Lê o arquivo como texto. O CSV da planilha de fechamento vem em Latin-1
 * (Windows); lido como UTF-8 os acentos viram "�". Tenta UTF-8 e, se
 * aparecer caractere estragado, relê como Windows-1252.
 */
function lerArquivo(arquivo) {
  const ler = (codificacao) =>
    new Promise((resolve, reject) => {
      const leitor = new FileReader();
      leitor.onload = () => resolve(String(leitor.result || ""));
      leitor.onerror = () => reject(leitor.error);
      leitor.readAsText(arquivo, codificacao);
    });
  return ler("UTF-8").then((texto) => (texto.includes("�") ? ler("windows-1252") : texto));
}

const fmt = (v) =>
  v === null || v === undefined ? "—" : new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2 }).format(v);
const dataBR = (iso) => (iso ? iso.split("-").reverse().join("/") : "—");

const COR_ACAO = { inserir: "green", atualizar: "blue", erro: "red", repetida: "orange" };
const ROTULO_ACAO = { inserir: "Nova", atualizar: "Atualiza", erro: "Erro", repetida: "Repetida" };

/**
 * Importação das cotações das agroações a partir do CSV de fechamento
 * (modelo: EMPRESA;Código;Data;Abert.;Mín.;Máx.;Média;Fechamento;(%);Compra;
 * Venda;Negócios;Qtde. títulos). Primeiro confere (a API simula e diz o que
 * acontece com cada linha), depois grava. Ação que já tem cotação no dia é
 * atualizada, não duplicada.
 */
export default function ModalImportarAgroacoes({ open, onClose, onImportado }) {
  const [texto, setTexto] = useState("");
  const [nomeArquivo, setNomeArquivo] = useState("");
  const [previa, setPrevia] = useState(null);
  const [carregando, setCarregando] = useState(false);

  const limpar = () => {
    setTexto("");
    setNomeArquivo("");
    setPrevia(null);
  };

  const fechar = () => {
    limpar();
    onClose && onClose();
  };

  const conferir = async (csv = texto) => {
    if (!csv.trim()) {
      message.warning("Cole o CSV ou escolha um arquivo.");
      return;
    }
    setCarregando(true);
    try {
      const r = await request("/cotacoes/importarCotacoesAcao", {
        method: "POST",
        body: JSON.stringify({ csv, simular: true }),
      });
      setPrevia(r);
      if (!r?.linhas?.length) message.warning("Nenhuma linha de cotação encontrada no CSV.");
    } catch (err) {
      console.log(err);
      message.error("Não foi possível conferir o CSV.");
    } finally {
      setCarregando(false);
    }
  };

  const importar = async () => {
    setCarregando(true);
    try {
      const r = await request("/cotacoes/importarCotacoesAcao", {
        method: "POST",
        body: JSON.stringify({ csv: texto }),
      });
      const { inseridas = 0, atualizadas = 0 } = r?.resumo || {};
      message.success(`Importação concluída: ${inseridas} nova(s), ${atualizadas} atualizada(s).`);
      onImportado && onImportado(r);
      fechar();
    } catch (err) {
      console.log(err);
      message.error("A importação falhou e nada foi gravado. Consulte um administrador.");
    } finally {
      setCarregando(false);
    }
  };

  const resumo = previa?.resumo;
  const aGravar = (resumo?.inserir || 0) + (resumo?.atualizar || 0);

  const colunas = [
    {
      title: "",
      dataIndex: "acao",
      width: 90,
      render: (a, r) => (
        <Tag color={COR_ACAO[a]} title={r.erros?.join("; ")}>
          {ROTULO_ACAO[a] || a}
        </Tag>
      ),
    },
    {
      title: "Código",
      dataIndex: "acoes_id",
      width: 90,
      render: (id, r) => (
        <>
          {id || "—"}
          {r.sem_cadastro && (
            <Tag color="gold" style={{ marginLeft: 4 }} title="Código sem cadastro em Agroações (Adicionar Nome)">
              sem cadastro
            </Tag>
          )}
        </>
      ),
    },
    { title: "Empresa", dataIndex: "empresa", ellipsis: true },
    { title: "Data", dataIndex: "acoes_data", width: 95, render: dataBR },
    { title: "Fech.", dataIndex: "acoes_valor", width: 80, align: "right", render: fmt },
    { title: "(%)", dataIndex: "acoes_porcentagem", width: 70, align: "right", render: fmt },
    { title: "Abert.", dataIndex: "acoes_abertura", width: 80, align: "right", render: fmt },
    { title: "Mín.", dataIndex: "acoes_min", width: 80, align: "right", render: fmt },
    { title: "Máx.", dataIndex: "acoes_max", width: 80, align: "right", render: fmt },
    {
      title: "Problema",
      dataIndex: "erros",
      render: (e) => (e?.length ? <Text type="danger">{e.join("; ")}</Text> : null),
    },
  ];

  return (
    <Modal
      title="Importar cotações (CSV)"
      open={open}
      onCancel={fechar}
      width={1100}
      footer={
        previa ? (
          <Space>
            <Button onClick={() => setPrevia(null)} disabled={carregando}>
              Voltar
            </Button>
            <Button type="primary" onClick={importar} loading={carregando} disabled={!aGravar}>
              Importar {aGravar} linha(s)
            </Button>
          </Space>
        ) : (
          <Space>
            <Button onClick={fechar}>Cancelar</Button>
            <Button type="primary" onClick={() => conferir()} loading={carregando} disabled={!texto.trim()}>
              Conferir
            </Button>
          </Space>
        )
      }
    >
      {!previa ? (
        <Space direction="vertical" style={{ width: "100%" }}>
          <Text type="secondary">
            Cole o conteúdo do CSV (ou as linhas copiadas do Excel) ou escolha o arquivo. Modelo:{" "}
            <Text code>EMPRESA;Código;Data;Abert.;Mín.;Máx.;Média;Fechamento;(%);Compra;Venda;Negócios;Qtde. títulos</Text>
          </Text>
          <Upload
            accept=".csv,.txt"
            showUploadList={false}
            beforeUpload={(arquivo) => {
              lerArquivo(arquivo)
                .then((t) => {
                  setTexto(t);
                  setNomeArquivo(arquivo.name);
                  conferir(t);
                })
                .catch(() => message.error("Não foi possível ler o arquivo."));
              return false;
            }}
          >
            <Button icon={<MdUploadFile />}>Escolher arquivo CSV</Button>
          </Upload>
          {nomeArquivo && <Text type="secondary">Arquivo: {nomeArquivo}</Text>}
          <Input.TextArea
            value={texto}
            onChange={(e) => {
              setTexto(e.target.value);
              setNomeArquivo("");
            }}
            placeholder="Cole o CSV aqui"
            autoSize={{ minRows: 10, maxRows: 18 }}
            style={{ fontFamily: "monospace", fontSize: 12 }}
          />
        </Space>
      ) : (
        <Space direction="vertical" style={{ width: "100%" }}>
          <Space wrap>
            <Tag color="green">{resumo.inserir} nova(s)</Tag>
            <Tag color="blue">{resumo.atualizar} atualiza(m) cotação já lançada no dia</Tag>
            {resumo.erros > 0 && <Tag color="red">{resumo.erros} com erro (não entram)</Tag>}
            <Text type="secondary">Data(s): {resumo.datas.map(dataBR).join(", ") || "—"}</Text>
          </Space>
          {resumo.sem_cadastro.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message={`Código(s) sem cadastro em Agroações: ${resumo.sem_cadastro.join(", ")}`}
              description="As cotações entram, mas o código não aparece na lista de ações desta tela até o nome ser cadastrado (botão Adicionar Nome)."
            />
          )}
          {previa.ignoradas?.length > 0 && (
            <Alert
              type="info"
              showIcon
              message={`${previa.ignoradas.length} linha(s) ignorada(s) por não terem data`}
              description={previa.ignoradas.map((l) => `linha ${l.linha_csv}: ${l.texto}`).join(" · ")}
            />
          )}
          <Table
            size="small"
            rowKey="linha_csv"
            columns={colunas}
            dataSource={previa.linhas}
            pagination={false}
            scroll={{ y: 420 }}
          />
        </Space>
      )}
    </Modal>
  );
}
